export type GrantNote = {
  id: number;
  body: string;
  author_id: number;
  author_name: string | null;
  created_at: string;
};

// Which record a note hangs off of. Notes share one endpoint shape across all
// three surfaces; only the nested path differs.
export type GrantNotable =
  | { type: "opportunity" }
  | { type: "milestone"; milestoneId: number }
  | { type: "task"; taskId: number };

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function notesPath(
  municipalityId: number,
  opportunityId: number,
  notable: GrantNotable,
): string {
  const base = `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}`;
  switch (notable.type) {
    case "milestone":
      return `${base}/milestones/${notable.milestoneId}/notes`;
    case "task":
      return `${base}/tasks/${notable.taskId}/notes`;
    default:
      return `${base}/notes`;
  }
}

export async function listGrantNotes(
  municipalityId: number,
  opportunityId: number,
  notable: GrantNotable,
): Promise<GrantNote[]> {
  const response = await fetch(notesPath(municipalityId, opportunityId, notable), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load notes");
  }
  return response.json();
}

export async function createGrantNote(
  municipalityId: number,
  opportunityId: number,
  notable: GrantNotable,
  body: string,
): Promise<GrantNote> {
  const response = await fetch(notesPath(municipalityId, opportunityId, notable), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_note: { body } }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.errors?.join(", ") ?? "Failed to add note");
  }
  return response.json();
}

export async function deleteGrantNote(
  municipalityId: number,
  opportunityId: number,
  notable: GrantNotable,
  noteId: number,
): Promise<void> {
  const response = await fetch(
    `${notesPath(municipalityId, opportunityId, notable)}/${noteId}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { "X-CSRF-Token": csrfToken() },
    },
  );
  if (!response.ok) {
    throw new Error("Failed to delete note");
  }
}
